import React from 'react';

export default function TolinoGuideBody() {
  return <div className="publishing-guide-body">
<h2>Tolino for Indie Authors: A Mini Handbook</h2>

<p><strong>Publish your German translation the way a German indie would: understand the Tolino ecosystem, price for the local market, and work within Germany's fixed-price rules.</strong></p>

<p>Aggregators such as Draft2Digital can distribute your ebook to Tolino, so using an aggregator does not mean missing the Tolino retail network. But publishing directly through tolino media can give you additional options that matter for a German edition, including direct access to Tolino's publishing tools, Skoobe and Onleihe distribution, and Tolino's own promotional opportunities.</p>

<h2>What Tolino is, and why it matters for German readers</h2>

<p>Tolino is not a store all by itself. It is an alliance of German booksellers formed in 2013 to stop Amazon eating the market: Thalia, the country's biggest chain, Hugendubel, Weltbild and others, plus the wholesaler Libri, which supplies about 1,300 independent bookshops. They share one e-reader and one ebook platform, however.</p>

<p>The alliance held its ground. Estimates of Tolino's share of German ebook sales have sat around 40% since 2015, roughly level with Kindle. No single figure is verifiable, but every estimate points the same way, and it is very different from the US situation, where Amazon takes 80–95% of the market.</p>

<p>The consequence: a German edition that exists only inside the Kindle ecosystem is invisible to something like half the readers you paid to reach.</p>

<h2>Two ways in, and why the German edition should go direct</h2>

<p>Every wide-publishing guide written in English says the same thing: log into Draft2Digital, tick "Tolino", done. Keep doing that for your English edition. For the German one, ask a different question: how would a German indie author publish this book? The answer is tolino media, the alliance's own KDP-style self-publishing portal.</p>

<p>Publishing an ebook there costs nothing. The free tier includes:</p>

<div className="publishing-guide-table" role="region" aria-label="Free publishing features" tabIndex={0}><table>
  <thead>
    <tr>
      <th scope="col">What you get</th>
      <th scope="col">Detail</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>ISBN</td>
      <td>Included</td>
    </tr>
    <tr>
      <td>File conversion</td>
      <td>Word or .odt to epub</td>
    </tr>
    <tr>
      <td>Updates</td>
      <td>Content and metadata changes, free</td>
    </tr>
    <tr>
      <td>German National Library delivery</td>
      <td>Optional, included</td>
    </tr>
    <tr>
      <td>Marketing campaigns</td>
      <td>Free placement, subject to availability</td>
    </tr>
    <tr>
      <td>Onleihe (library lending) and Skoobe (subscription)</td>
      <td>Available as direct opt-in channels through tolino media. Draft2Digital does not currently list Skoobe or Onleihe among its distribution channels.</td>
    </tr>
    <tr>
      <td>Opt-in retail channels</td>
      <td>Amazon, Apple Books and Google Play (since 2021)</td>
    </tr>
  </tbody>
</table></div>

<p>Three rules for choosing the route:</p>

<ol>
  <li>Use tolino media to reach the alliance shops, Onleihe and Skoobe. That is what it is built for.</li>
  <li>Do not use it as your route to everyone else. The opt-in channels pay a lower rate, as shown below. Keep your existing distribution for Amazon, Apple and Google.</li>
  <li>Check your Amazon exclusivity first. tolino media takes non-exclusive rights only, but if the German ebook is enrolled in KDP Select, Amazon's rules keep it off every alliance shop. Print is exempt from Select; the ebook is not. Pull it out of Select before you upload.</li>
</ol>

<h2>Money: the 87%, the €3.99 cliff, payouts and the fixed-price law</h2>

<h3>What 87% actually pays</h3>

<p>The headline rate is 87% for ebooks priced €3.99 to €24.98 and sold in alliance shops. It is 87% of net proceeds, meaning price minus VAT, trade discounts and distribution costs, not 87% of the sticker price. tolino media's own worked examples, from its pricing page as of early 2025:</p>

<div className="publishing-guide-table" role="region" aria-label="Royalty examples" tabIndex={0}><table>
  <thead>
    <tr>
      <th scope="col">Price and channel</th>
      <th scope="col">Rate</th>
      <th scope="col">You receive, approx.</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>€3.99, alliance shops such as Thalia and Hugendubel</td>
      <td>87% of net</td>
      <td>€2.44</td>
    </tr>
    <tr>
      <td>€2.99, alliance shops</td>
      <td>67% of net</td>
      <td>€1.40</td>
    </tr>
    <tr>
      <td>€3.99, Google Play via tolino media</td>
      <td>70% of net</td>
      <td>€1.64</td>
    </tr>
  </tbody>
</table></div>

<p>Two things to do with that table:</p>

<ul>
  <li>Price at €3.99 or above. The threshold is a cliff, not a slope: under €3.99 the alliance rate drops to 67%. The US reflex of launching at a $2.99 equivalent is punished here, and German readers do not expect it anyway.</li>
</ul>

<h3>When the money arrives</h3>

<p>There are four settlement streams, each with its own payout threshold:</p>

<div className="publishing-guide-table" role="region" aria-label="Settlement streams" tabIndex={0}><table>
  <thead>
    <tr>
      <th scope="col">Stream</th>
      <th scope="col">Paid</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Alliance-shop ebooks</td>
      <td>Monthly, about 2–3 weeks after month end</td>
    </tr>
    <tr>
      <td>Libraries</td>
      <td>About 8–9 weeks</td>
    </tr>
    <tr>
      <td>Print</td>
      <td>Quarterly</td>
    </tr>
    <tr>
      <td>Audiobooks</td>
      <td>About 12 weeks</td>
    </tr>
  </tbody>
</table></div>

<p>The threshold is €20 per stream for SEPA bank accounts and €500 per stream for non-SEPA accounts. A US author paid into a US bank waits until each stream crosses €500; balances under the threshold are paid out at least once a year. If you can be paid into a SEPA-reachable euro account, the wait shrinks to €20.</p>

<p>Non-German residents are handled under the reverse-charge VAT procedure. However, contrary to some popular myths, there is no extra step that a US resident must go through to publish there.</p>

<h3>The fixed-price law (Buchpreisbindungsgesetz)</h3>

<p>Germany has a federal statute setting the retail price of your novel. It applies to self-publishers, explicitly covers ebooks, and has one rule: each edition must cost the same at every retailer at all times.</p>

<p>What that does to your promo playbook:</p>

<ul>
  <li><strong>Price promotions are allowed, but Germany's fixed-price rules still apply.</strong> Tolino media currently allows an ebook promotion to run for up to four weeks, followed by a four-week pause before another promotion. If the ebook is also sold through platforms not supplied by tolino media, the promotional price needs to be coordinated across those retailers as required by German fixed-price law.</li>
  <li><strong>Every change happens everywhere at once.</strong> Drop the price at Thalia and you must drop it at Amazon, Apple and every other store the same day. A per-store sale is a violation.</li>
  <li><strong>Different editions can carry different prices.</strong> Ebook, paperback and hardcover editions can each have different prices.</li>
</ul>

<h2>Visibility: editorial slots, no pay-to-play</h2>

<p>There is no Select-style pot to buy into. Visibility on Tolino is largely editorial, and charmingly analogue: the alliance shops run price-promo newsletters, seasonal and regional campaigns, and features such as an author-of-the-month slot. tolino media curates who gets in. Participation is free, needs no exclusivity, and your royalty percentage holds even at the promo price.</p>

<p>How to get in:</p>

<ol>
  <li>Publish direct. Campaign placement is part of tolino media's free tier; you cannot pitch for it from an aggregator.</li>
  <li>Pitch your own book for a slot at <a href="mailto:publishing@tolino.media">publishing@tolino.media</a>, a couple of months ahead of the date you want.</li>
  <li>If you have a series, upload a bundle. German buyers in tolino-powered shops love book boxes. Put "bundle" and "Sammelband" in your keywords field; that tip came from a tolino manager directly.</li>
</ol>

<h2>Quick-start checklist</h2>

<ul>
  <li>Pull the German ebook out of KDP Select. Print can stay.</li>
  <li>Open a free tolino media account and upload the German edition direct. Take the free ISBN if you need one.</li>
  <li>Tick the Onleihe and Skoobe boxes at upload.</li>
  <li>For a series bundle, add "bundle" and "Sammelband" to the keywords.</li>
  <li>Leave the opt-in channels such as Amazon, Apple and Google Play to your existing distribution unless you have a reason not to.</li>
  <li>Price the ebook at €3.99 or above, and set the identical price on every store the same day.</li>
  <li>Publishing print on both KDP and tolino media? Run tolino's print calculator first and match the Amazon price to it.</li>
  <li>Pitch for campaigns at publishing@tolino.media at least two months ahead.</li>
</ul>
  </div>;
}
